import React, { useEffect, useState } from 'react';
import { useFormContext } from 'react-hook-form';
import type { RegisterOptions } from 'react-hook-form';
import { Check, X } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { Field, FieldError, FieldLabel } from '@/components/ui/field';
import { Textarea } from '@/components/ui/textarea';
import { useTaskFieldEdit } from './TaskFieldEditContext';
import type { TaskPencilFieldKey } from './taskFieldEditModel';

type TicketTextAreaFieldProps = {
  title: string;
  fieldName: TaskPencilFieldKey;
  rules?: RegisterOptions;
  placeholder?: string;
  disabled?: boolean;
};

export const TicketTextAreaField: React.FC<TicketTextAreaFieldProps> = ({
  title,
  fieldName,
  rules,
  placeholder,
  disabled,
}) => {
  const {
    register,
    getValues,
    setValue,
    formState: { errors },
  } = useFormContext();
  const { autoPersist, persistTip, editingFieldKey, beginEdit, endEdit } =
    useTaskFieldEdit();
  const error = errors[fieldName]?.message as string | undefined;
  const inputId = `${fieldName}-textarea`;
  const isEditing = autoPersist && editingFieldKey === fieldName;
  const [draft, setDraft] = useState(() => String(getValues(fieldName) ?? ''));

  useEffect(() => {
    if (isEditing) {
      setDraft(String(getValues(fieldName) ?? ''));
    }
  }, [fieldName, getValues, isEditing]);

  if (!autoPersist) {
    return (
      <Field data-invalid={error ? true : undefined}>
        <FieldLabel htmlFor={inputId}>{title}</FieldLabel>
        <Textarea
          id={inputId}
          placeholder={placeholder}
          disabled={disabled}
          rows={4}
          aria-invalid={error ? true : undefined}
          {...register(fieldName, rules)}
        />
        <FieldError>{error}</FieldError>
      </Field>
    );
  }

  const stored = String(getValues(fieldName) ?? '');

  const commit = () => {
    setValue(fieldName, draft, { shouldDirty: true, shouldValidate: true });
    endEdit();
    void persistTip();
  };

  const cancel = () => {
    setDraft(stored);
    endEdit();
  };

  return (
    <Field data-invalid={error ? true : undefined}>
      <div className="tw:flex tw:items-center tw:justify-between tw:gap-2">
        <FieldLabel htmlFor={inputId}>{title}</FieldLabel>
        {disabled || !isEditing ? null : (
          <div className="tw:flex tw:gap-1">
            <Button
              type="button"
              variant="ghost"
              size="icon-sm"
              aria-label={`Save ${title}`}
              onClick={commit}
            >
              <Check className="tw:size-4" />
            </Button>
            <Button
              type="button"
              variant="ghost"
              size="icon-sm"
              aria-label={`Cancel ${title}`}
              onClick={cancel}
            >
              <X className="tw:size-4" />
            </Button>
          </div>
        )}
      </div>
      {isEditing ? (
        <Textarea
          id={inputId}
          placeholder={placeholder}
          disabled={disabled}
          rows={4}
          value={draft}
          onChange={(event) => setDraft(event.target.value)}
          aria-invalid={error ? true : undefined}
          autoFocus
        />
      ) : (
        <Textarea
          id={inputId}
          readOnly
          rows={4}
          disabled={disabled}
          placeholder={placeholder}
          value={stored}
          aria-label={`Edit ${title}`}
          aria-invalid={error ? true : undefined}
          onClick={() => {
            if (!disabled) beginEdit(fieldName);
          }}
          onKeyDown={(event) => {
            if (disabled) return;
            if (event.key === 'Enter' || event.key === ' ') {
              event.preventDefault();
              beginEdit(fieldName);
            }
          }}
          className="tw:cursor-pointer tw:transition-colors tw:hover:border-border tw:hover:bg-muted/40 tw:focus-visible:ring-ring"
        />
      )}
      <FieldError>{error}</FieldError>
    </Field>
  );
};
